import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import Sidebar from "../../components/Dashboard/Sidebar";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiBox,
  FiDollarSign,
  FiLayers,
  FiHash,
  FiTruck,
  FiImage,
  FiUpload,
  FiPackage,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { useAuth } from "../../context/auth";
import Swal from "sweetalert2";
import { getImageUrl } from "../../utils/imageHelper";

const MAX_IMAGE_SIZE = 1024 * 1024;

const UpdateProduct = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [auth] = useAuth();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "",
    description: "",
    actualPrice: "",
    price: "",
    category: "",
    quantity: "",
    stock: "",
    shipping: "",
    sizes: [],
    colors: [],
  });
  const [sizeInput, setSizeInput] = useState("");

  const [colorInput, setColorInput] = useState({
    name: "",
    hex: "#000000",
  });
  const addSize = () => {
    const size = sizeInput.trim();

    if (!size) return;

    if (form.sizes.includes(size)) {
      return toast.error("Size already exists");
    }

    setForm({
      ...form,
      sizes: [...form.sizes, size],
    });

    setSizeInput("");
  };

  const removeSize = (size) => {
    setForm({
      ...form,
      sizes: form.sizes.filter((s) => s !== size),
    });
  };

  const addColor = () => {
    const name = colorInput.name.trim();

    if (!name) return;

    if (form.colors.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      return toast.error("Color already exists");
    }

    setForm({
      ...form,
      colors: [...form.colors, colorInput],
    });

    setColorInput({
      name: "",
      hex: "#000000",
    });
  };

  const removeColor = (name) => {
    setForm({
      ...form,
      colors: form.colors.filter((c) => c.name !== name),
    });
  };
  const [photos, setPhotos] = useState([]); // naye selected files (array)
  const [existingPhotos, setExistingPhotos] = useState([]); // DB se aaye purane photos (paths)
  const [id, setId] = useState("");
  const [loading, setLoading] = useState(false);

  // Get single product
  useEffect(() => {
    axios
      .get(`/api/v1/product/get-product/${params.slug}`)
      .then((res) => {
        const prod = res.data.product;
        setForm({
          name: prod.name,
          description: prod.description,
          actualPrice: prod.actualPrice || "",
          price: prod.price,
          category: prod.category._id,
          quantity: prod.quantity,
          stock: prod.stock || "",
          shipping:
            prod.shipping === true ||
            prod.shipping === 1 ||
            prod.shipping === "yes"
              ? "1"
              : "0",
          sizes: prod.sizes || [],
          colors: prod.colors || [],
        });
        setId(prod._id);
        setExistingPhotos(prod.photos || []);
      })
      .catch(() => toast.error("Failed to load product"));
    // eslint-disable-next-line
  }, [params.slug]);

  useEffect(() => {
    axios
      .get("/api/v1/category/get-category")
      .then((res) => {
        if (res.data?.success) setCategories(res.data.category);
      })
      .catch(() => toast.error("Failed to load categories"));
  }, []);

  // Set new image files
  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const oversized = files.find((f) => f.size > MAX_IMAGE_SIZE);
    if (oversized) {
      toast.error(
        `"${oversized.name}" is ${(oversized.size / 1024 ** 2).toFixed(
          2,
        )}MB. Each photo must be less than 1MB.`,
      );
      return;
    }

    if (files.length > 5) {
      toast.error("You can upload a maximum of 5 photos");
      return;
    }

    setPhotos(files);
  };

  const handleRemoveNewPhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const productData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          productData.append(key, JSON.stringify(value));
        } else {
          productData.append(key, value);
        }
      });

      photos.forEach((file) => productData.append("photos", file));

      const { data } = await axios.put(
        `/api/v1/product/update-product/${id}`,
        productData,
      );
      if (data?.success) {
        toast.success("Product Updated Successfully");
        navigate("/dashboard/admin/products");
      } else {
        toast.error(data?.message || data?.error || "Failed to update product");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Something went wrong";
      toast.error(msg);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: "Delete this Product?",
      text: "It will not be revert.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Delete",
      background: "#111827",
      color: "#fff",
    });
    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/v1/product/delete-product/${id}`);
        Swal.fire({
          title: "Deleted!",
          text: "Item has been Deleted.",
          icon: "success",
          background: "#111827",
          color: "#fff",
        });
        navigate("/dashboard/admin/products");
      } catch {
        Swal.fire({
          title: "Error",
          text: "Failed to delete Item",
          icon: "error",
          background: "#111827",
          color: "#fff",
        });
      }
    }
  };

  return (
    <Layout title="Update Product">
      <div className="min-h-screen bg-gray-50 mt-20">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Update Product
              </h1>
              <p className="text-gray-600 mt-2">
                Update product details or delete product.
              </p>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row lg:gap-8">
            <aside className="w-full lg:w-80 flex-shrink-0 mb-6 lg:mb-0">
              <Sidebar auth={auth} activeTab="products" />
            </aside>
            <main className="flex-1">
              <div className="bg-white rounded-xl shadow border border-gray-100 p-8">
                <form
                  onSubmit={handleUpdate}
                  className="space-y-6 flex-grow flex flex-col"
                >
                  <div className="flex-grow space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Product Name
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiBox className="text-gray-400" size={18} />
                          </div>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Enter product name"
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="actualPrice"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Actual Price (MRP)
                        </label>

                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiDollarSign className="text-gray-400" size={18} />
                          </div>

                          <input
                            type="number"
                            id="actualPrice"
                            name="actualPrice"
                            value={form.actualPrice}
                            onChange={handleChange}
                            min="0"
                            placeholder="Enter Actual Price"
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="price"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Selling Price
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiDollarSign className="text-gray-400" size={18} />
                          </div>
                          <input
                            type="number"
                            id="price"
                            name="price"
                            value={form.price}
                            onChange={handleChange}
                            min="0"
                            placeholder="Enter price"
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="category"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Category
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiLayers className="text-gray-400" size={18} />
                          </div>
                          <select
                            id="category"
                            name="category"
                            value={form.category}
                            onChange={handleChange}
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          >
                            <option value="">Select category</option>
                            {categories.map((c) => (
                              <option value={c._id} key={c._id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="stock"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Stock
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiPackage className="text-gray-400" size={18} />
                          </div>
                          <input
                            type="number"
                            id="stock"
                            name="stock"
                            value={form.stock}
                            min="0"
                            onChange={handleChange}
                            placeholder="Stock"
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="quantity"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Quantity
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiHash className="text-gray-400" size={18} />
                          </div>
                          <input
                            type="number"
                            id="quantity"
                            name="quantity"
                            value={form.quantity}
                            min="0"
                            onChange={handleChange}
                            placeholder="Quantity"
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="shipping"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Shipping
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiTruck className="text-gray-400" size={18} />
                          </div>
                          <select
                            id="shipping"
                            name="shipping"
                            value={form.shipping}
                            onChange={handleChange}
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          >
                            <option value="">Select option</option>
                            <option value="1">Yes</option>
                            <option value="0">No</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      {/* Sizes */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Available Sizes
                        </label>

                        <div className="flex flex-col sm:flex-row gap-3">
                          <input
                            type="text"
                            placeholder="e.g. S, XL, 42"
                            value={sizeInput}
                            onChange={(e) => setSizeInput(e.target.value)}
                            className="w-full sm:flex-1 border rounded-lg px-4 py-3"
                          />

                          <button
                            type="button"
                            onClick={addSize}
                            className="w-full sm:w-[110px] h-[50px] bg-black text-white rounded-lg flex items-center justify-center flex-shrink-0"
                          >
                            Add
                          </button>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-3">
                          {form.sizes.map((size) => (
                            <div
                              key={size}
                              className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2"
                            >
                              {size}
                              <button
                                type="button"
                                onClick={() => removeSize(size)}
                                className="text-red-600"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Colors */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Available Colors
                        </label>

                        <div className="flex flex-col sm:grid sm:grid-cols-[1fr_70px_110px] gap-3">
                          <input
                            type="text"
                            placeholder="Color Name"
                            value={colorInput.name}
                            onChange={(e) =>
                              setColorInput({
                                ...colorInput,
                                name: e.target.value,
                              })
                            }
                            className="w-full border rounded-lg px-4 py-3"
                          />

                          <input
                            type="color"
                            value={colorInput.hex}
                            onChange={(e) =>
                              setColorInput({
                                ...colorInput,
                                hex: e.target.value,
                              })
                            }
                            className="w-full sm:w-[70px] h-[50px] rounded-lg border cursor-pointer"
                          />

                          <button
                            type="button"
                            onClick={addColor}
                            className="w-full sm:w-[110px] h-[50px] bg-black text-white rounded-lg flex items-center justify-center flex-shrink-0"
                          >
                            Add
                          </button>
                        </div>

                        <div className="flex flex-wrap gap-3 mt-4">
                          {form.colors.map((color) => (
                            <div
                              key={color.name}
                              className="flex items-center gap-2 border rounded-full px-3 py-2"
                            >
                              <div
                                className="w-5 h-5 rounded-full border"
                                style={{ background: color.hex }}
                              />
                              <span>{color.name}</span>
                              <button
                                type="button"
                                onClick={() => removeColor(color.name)}
                                className="text-red-600"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="description"
                        className="block text-sm font-semibold text-gray-900 mb-2"
                      >
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        placeholder="Enter product description"
                        rows={4}
                        className="w-full pl-4 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all resize-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <FiUpload /> Upload/Replace Photos (Max 1MB each, up to
                        5)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotoChange}
                        className="block w-full mt-2"
                      />
                      <div className="flex gap-4 mt-3 flex-wrap">
                        {photos.length > 0
                          ? photos.map((file, i) => (
                              <div className="relative group w-24" key={i}>
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`New ${i}`}
                                  className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                                />
                                <button
                                  type="button"
                                  className="absolute top-2 right-2 bg-white/80 rounded-full text-red-600 shadow-md p-1 opacity-70 group-hover:opacity-100 transition"
                                  onClick={() => handleRemoveNewPhoto(i)}
                                  title="Remove"
                                >
                                  <FiX size={16} />
                                </button>
                                <div className="absolute left-2 bottom-2 text-[10px] bg-white/90 rounded px-1 py-0.5">
                                  {(file.size / 1024).toFixed(0)} KB
                                </div>
                              </div>
                            ))
                          : existingPhotos.map((path, i) => (
                              <div className="relative group w-24" key={i}>
                                <img
                                  src={getImageUrl(path)}
                                  alt={`Existing ${i}`}
                                  className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                                />
                              </div>
                            ))}
                        {photos.length === 0 && existingPhotos.length === 0 && (
                          <div className="w-24 h-24 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center">
                            <FiImage size={24} className="text-gray-400" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 pt-6 mt-auto flex flex-col sm:flex-row items-center justify-end gap-4">
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all flex items-center justify-center gap-2"
                    >
                      <FiTrash2 /> Delete
                    </button>
                    <div className="flex gap-4 w-full sm:w-auto">
                      <button
                        type="button"
                        onClick={() => window.history.back()}
                        className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 sm:flex-none px-8 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-black transition-all disabled:opacity-50"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Updating...
                          </span>
                        ) : (
                          "Update Product"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UpdateProduct;
